import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchAllShifts,
  createShift,
  updateShift,
  deleteShift,
} from "../../store/allocation/shiftsSlice";
import { Shifts as ShiftType } from "../../types/shiftsTypes";
import { Helmet } from "react-helmet-async";
import {
  FiAlertCircle,
  FiClock,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminTableCard,
  AdminTableState,
  AdminSoftInput,
  AdminSoftSelect,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const hours = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
const meridiem = ["AM", "PM"];

const parseTime = (time: string) => {
  const match = time?.match(/^(\d{1,2}):\d{2}\s*(AM|PM)$/i);
  if (!match) return { hour: "", meridiem: "" };
  return {
    hour: String(Number(match[1])),
    meridiem: match[2].toUpperCase(),
  };
};

const emptyForm = () => ({
  shiftName: "",
  startTime: { hour: "", meridiem: "" },
  endTime: { hour: "", meridiem: "" },
});

const Shifts = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { allShifts, loading, error } = useSelector(
    (state: RootState) => state.shifts,
  );

  const [openModal, setOpenModal] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState<number | null>(null);
  const [shiftName, setShiftName] = useState("");
  const [startTime, setStartTime] = useState({ hour: "", meridiem: "" });
  const [endTime, setEndTime] = useState({ hour: "", meridiem: "" });

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchAllShifts());
  }, [dispatch]);

  const resetForm = () => {
    const empty = emptyForm();
    setShiftName(empty.shiftName);
    setStartTime(empty.startTime);
    setEndTime(empty.endTime);
    setEditingShiftId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setOpenModal(true);
  };

  const openEditModal = (shift: ShiftType) => {
    setEditingShiftId(shift.shift_id);
    setShiftName(shift.shift_name);
    setStartTime(parseTime(shift.start_time));
    setEndTime(parseTime(shift.end_time));
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const handleSaveShift = () => {
    if (
      !shiftName ||
      !startTime.hour ||
      !startTime.meridiem ||
      !endTime.hour ||
      !endTime.meridiem
    )
      return;

    const formattedStart = `${startTime.hour}:00 ${startTime.meridiem}`;
    const formattedEnd = `${endTime.hour}:00 ${endTime.meridiem}`;

    const action =
      editingShiftId !== null
        ? updateShift({
            shiftId: editingShiftId,
            shiftName,
            startTime: formattedStart,
            endTime: formattedEnd,
          })
        : createShift({
            shiftName,
            startTime: formattedStart,
            endTime: formattedEnd,
          });

    dispatch(action).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        closeModal();
      }
    });
  };

  const confirmDelete = () => {
    if (confirmDeleteId !== null) {
      dispatch(deleteShift(confirmDeleteId));
      setConfirmDeleteId(null);
    }
  };

  const isEditing = editingShiftId !== null;
  const isFormComplete = Boolean(
    shiftName.trim() &&
      startTime.hour &&
      startTime.meridiem &&
      endTime.hour &&
      endTime.meridiem,
  );

  const colCount = 4;

  return (
    <>
      <Helmet>
        <title>Shifts</title>
      </Helmet>
        <AdminPageShell accent="#345952">
          <AdminPageHeader
            title="Shift Management"
            subtitle="Create and maintain the schedules available to your team."
          >
            <AdminPrimaryButton onClick={openCreateModal} className="px-4 py-2.5">
              <FiPlus size={16} />
              Add shift
            </AdminPrimaryButton>
          </AdminPageHeader>

          <section className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-4 rounded-2xl border border-[#E8E8E4] bg-white p-5 shadow-sm">
              <div className="rounded-xl bg-[#E8F0EE] p-3 text-[#345952]">
                <FiClock size={23} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#4A4A48]">
                  {allShifts.length}
                </p>
                <p className="text-sm text-[#8A8A85]">Configured shifts</p>
              </div>
            </div>
            <div className="rounded-2xl border border-[#E8E8E4] bg-gradient-to-r from-[#1c3b41] to-[#345952] p-5 text-white shadow-sm">
              <p className="text-sm font-medium text-white/75">
                Schedule coverage
              </p>
              <p className="mt-1 font-semibold">
                {allShifts.length
                  ? "Your team has active scheduling options"
                  : "Add your first shift to get started"}
              </p>
            </div>
          </section>

          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <FiAlertCircle />
              {error}
            </div>
          )}

          <AdminTableCard>
            <table className="w-full min-w-[680px] table-auto">
              <thead>
                <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                  <th className={adminTableHead}>Shift</th>
                  <th className={adminTableHead}>Start time</th>
                  <th className={adminTableHead}>End time</th>
                  <th className={`${adminTableHead} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && allShifts.length === 0 ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="loading"
                    message="Loading shifts…"
                  />
                ) : allShifts.length === 0 ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="empty"
                    message="No shifts configured"
                    action={
                      <button
                        type="button"
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-1.5 text-[#345952] hover:underline"
                      >
                        <FiPlus className="text-[10px]" />
                        Create first shift
                      </button>
                    }
                  />
                ) : (
                  allShifts.map((shift) => (
                    <tr
                      key={shift.shift_id}
                      className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                    >
                      <td className={adminTableCell}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F0EE] text-[#345952]">
                            <FiClock />
                          </div>
                          <div>
                            <p className="font-semibold text-[#4A4A48]">
                              {shift.shift_name}
                            </p>
                            <p className="text-xs text-[#AAAAAA]">
                              Shift #{shift.shift_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className={adminTableCell}>
                        <span className="rounded-lg bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          {shift.start_time}
                        </span>
                      </td>
                      <td className={adminTableCell}>
                        <span className="rounded-lg bg-amber-50 px-3 py-1.5 font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                          {shift.end_time}
                        </span>
                      </td>
                      <td className={`${adminTableCell} text-right`}>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(shift)}
                            aria-label={`Edit ${shift.shift_name}`}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-[#DDDDD8] px-3 py-2 text-xs font-medium text-[#4A4A48] transition hover:border-[#345952] hover:bg-[#E8F0EE] hover:text-[#345952]"
                          >
                            <FiEdit2 />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(shift.shift_id)}
                            aria-label={`Delete ${shift.shift_name}`}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-50"
                          >
                            <FiTrash2 />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableCard>
        </AdminPageShell>

      {openModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shift-modal-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-[#E8E8E4] px-6 py-5">
              <div>
                <h2
                  id="shift-modal-title"
                  className="text-xl font-bold text-[#4A4A48]"
                >
                  {isEditing ? "Edit shift" : "Create a new shift"}
                </h2>
                <p className="mt-1 text-sm text-[#8A8A85]">
                  Set the shift name and working hours.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close dialog"
                className="rounded-lg p-2 text-[#AAAAAA] transition hover:bg-[#F8F8F6] hover:text-[#4A4A48]"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-5 p-6">
              <div>
                <label
                  htmlFor="shift-name"
                  className="mb-1.5 block text-sm font-semibold text-[#4A4A48]"
                >
                  Shift name
                </label>
                <AdminSoftInput
                  id="shift-name"
                  type="text"
                  placeholder="e.g. Morning Shift"
                  value={shiftName}
                  onChange={(event) => setShiftName(event.target.value)}
                  autoFocus
                  className="w-full py-2.5"
                />
              </div>

              {[
                {
                  label: "Start time",
                  value: startTime,
                  setValue: setStartTime,
                },
                {
                  label: "End time",
                  value: endTime,
                  setValue: setEndTime,
                },
              ].map(({ label, value, setValue }) => (
                <div key={label}>
                  <label className="mb-1.5 block text-sm font-semibold text-[#4A4A48]">
                    {label}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <AdminSoftSelect
                      aria-label={`${label} hour`}
                      value={value.hour}
                      onChange={(event) =>
                        setValue((previous) => ({
                          ...previous,
                          hour: event.target.value,
                        }))
                      }
                    >
                      <option value="">Select hour</option>
                      {hours.map((hour) => (
                        <option key={hour} value={hour}>
                          {hour}:00
                        </option>
                      ))}
                    </AdminSoftSelect>
                    <AdminSoftSelect
                      aria-label={`${label} meridiem`}
                      value={value.meridiem}
                      onChange={(event) =>
                        setValue((previous) => ({
                          ...previous,
                          meridiem: event.target.value,
                        }))
                      }
                    >
                      <option value="">AM / PM</option>
                      {meridiem.map((period) => (
                        <option key={period} value={period}>
                          {period}
                        </option>
                      ))}
                    </AdminSoftSelect>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 border-t border-[#E8E8E4] bg-[#F8F8F6] px-6 py-4 rounded-b-2xl">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-xl border border-[#DDDDD8] bg-white px-4 py-2.5 text-sm font-semibold text-[#4A4A48] transition hover:bg-[#F8F8F6]"
              >
                Cancel
              </button>
              <AdminPrimaryButton
                onClick={handleSaveShift}
                disabled={!isFormComplete || loading}
                className="px-4 py-2.5 text-sm"
              >
                {loading
                  ? "Saving..."
                  : isEditing
                    ? "Update shift"
                    : "Create shift"}
              </AdminPrimaryButton>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-shift-title"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
              <FiTrash2 size={22} />
            </div>
            <h2
              id="delete-shift-title"
              className="text-lg font-bold text-[#4A4A48]"
            >
              Delete this shift?
            </h2>
            <p className="mt-2 text-sm leading-6 text-[#8A8A85]">
              This action cannot be undone. Employees assigned to this shift may
              need to be reassigned.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                disabled={loading}
                className="rounded-xl border border-[#DDDDD8] px-4 py-2.5 text-sm font-semibold text-[#4A4A48] transition hover:bg-[#F8F8F6] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                <FiTrash2 />
                {loading ? "Deleting..." : "Delete shift"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Shifts;
